import { UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApi } from '@fortem-monorepo/react-spa'
import { ICReport } from '@fortem-monorepo/web-sdk'

import { queryKeys } from './keys'

type Api = ReturnType<typeof useApi>

/**
 * Create a type to use in custom hook wrappers
 */
export type OverrideQueryOptions<Tfn extends (...args: any[]) => Promise<any> = any> = Omit<
  UseQueryOptions<Awaited<ReturnType<Tfn>>, Error, Awaited<ReturnType<Tfn>>>,
  'queryFn' | 'queryKey'
>

/**
 * Invalidate an IC Report endpoint
 */
export const useInvalidateICData = (endpoint: ICReport.GetICReportDataParams['endpoint']) => {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.endpoint(endpoint) })
  }
}

/**
 * Get the template data for IC Report page for a given fund
 */
export const useICReportTemplateQuery = (params: ICReport.GetICReportTemplateParams) => {
  const api = useApi()

  const { fund_name, st_dt, ed_dt } = params

  const query = useQuery({
    queryKey: ['ic_report/template', [fund_name, st_dt, ed_dt]],
    queryFn: () => api.icReport.getTemplate(params),
    enabled: !!fund_name,
  })

  return {
    query,
    data: query.data,
  }
}

/**
 * Another wrapper to prevent code duplication to save time.
 *
 * Pass in the endpoint for the query key. And pass in the query function
 * for type inference and the query function itself.
 */
const createICReportQueryHook = <TFn extends (...args: any[]) => Promise<any> = any>(
  endpoint: ICReport.GetICReportDataParams['endpoint'],
  getApiFn: (api: Api) => TFn,
) => {
  return (
    params: Omit<ICReport.GetICReportDataParams, 'endpoint'>,
    options: OverrideQueryOptions<TFn> = {},
  ) => {
    const api = useApi()
    const apiFn = getApiFn(api)
    const defaultEnabled = !!params.fund_name && !!params.st_dt

    const query = useQuery({
      queryKey: queryKeys.detail(endpoint, params),
      queryFn: () => apiFn(params),
      enabled: defaultEnabled,
      ...options,
    })

    return query
  }
}

//
// FUND LEVEL
//

export const fundLevel = {
  useFundLevel: createICReportQueryHook(
    'fund_level',
    (api) => api.icReport.fund_level.getFundLevel,
  ),
  useMetrics: createICReportQueryHook(
    'fund_level_metrics',
    (api) => api.icReport.fund_level.getFundLevelMetrics,
  ),
  useRebasedNav: createICReportQueryHook(
    'rebased_nav',
    (api) => api.icReport.fund_level.getRebasedNav,
  ),
  useBucketedAttributions: createICReportQueryHook(
    'attributions_bucketed',
    (api) => api.icReport.fund_level.getBucketedAttributions,
  ),
  useTrackingDifferences: createICReportQueryHook(
    'tracking_differences',
    (api) => api.icReport.fund_level.getTrackingDifferences,
  ),
  useLiquidityReporting: createICReportQueryHook(
    'liquidity_reporting',
    (api) => api.icReport.fund_level.getLiquidityReporting,
  ),
  useStressTest: createICReportQueryHook(
    'bear_stress_test',
    (api) => api.icReport.fund_level.getStressTest,
  ),
  useHistoricalEvents: createICReportQueryHook(
    'historical_events',
    (api) => api.icReport.fund_level.getHistoricalEvents,
  ),
  useHistoricalEventsNew: createICReportQueryHook(
    'scenario_analysis_fund_level',
    (api) => api.icReport.fund_level.getHistoricalEventsNew,
  ),
}

//
// BETA
//

export const beta = {
  useDetailedBreakdown: createICReportQueryHook(
    'detailed_breakdown',
    (api) => api.icReport.beta.getDetailedBreakdown,
  ),
  useAggregatedBreakdown: createICReportQueryHook(
    'aggregated_breakdown',
    (api) => api.icReport.beta.getAggregatedBreakdown,
  ),
  useOptions: createICReportQueryHook('beta_options', (api) => api.icReport.beta.getOptions),
  useNewData: createICReportQueryHook('new_data', (api) => api.icReport.beta.getNewData),
  useOldData: createICReportQueryHook('old_data', (api) => api.icReport.beta.getOldData),
  useCapitalGrowth: createICReportQueryHook(
    'capital_growth',
    (api) => api.icReport.beta.getCapitalGrowth,
  ),
  useCapitalGrowthBarriers: createICReportQueryHook(
    'capital_growth_barriers',
    (api) => api.icReport.beta.getCapitalGrowthBarriers,
  ),
  useCapitalProtection: createICReportQueryHook(
    'capital_protection',
    (api) => api.icReport.beta.getCapitalProtection,
  ),
  useCapitalProtectionBarriers: createICReportQueryHook(
    'capital_protection_barriers',
    (api) => api.icReport.beta.getCapitalProtectionBarriers,
  ),
  useHistoricalEvents: createICReportQueryHook(
    'historical_events_beta',
    (api) => api.icReport.beta.getHistoricalEvents,
  ),
  useHistoricalEventsNew: createICReportQueryHook(
    'scenario_analysis_beta',
    (api) => api.icReport.beta.getHistoricalEventsNew,
  ),
}

export const income = {
  useDuration: createICReportQueryHook('income_duration', (api) => api.icReport.income.getDuration),
  useRatingChange: createICReportQueryHook(
    'rating_change',
    (api) => api.icReport.income.getRatingChange,
  ),
  useEodCash: createICReportQueryHook('eod_cash_balances', (api) => api.icReport.income.getEodCash),
  useBucketVsOvernightDifferences: createICReportQueryHook(
    'bucket_vs_overnight',
    (api) => api.icReport.income.getBucketVsOvernightDifferences,
  ),
  useLockedYield: createICReportQueryHook(
    'locked_yield',
    (api) => api.icReport.income.getLockedYield,
  ),
  usePerIssuer: createICReportQueryHook('per_issuer', (api) => api.icReport.income.getPerIssuer),
}

//
// COMMODITIES
//

export const commodities = {
  useHistoricalEventsBcom: createICReportQueryHook(
    'historical_events_bcom',
    (api) => api.icReport.commodities.getHistoricalEventsBcom,
  ),
  useCurves: createICReportQueryHook(
    'commodity_curves',
    (api) => api.icReport.commodities.getCurves,
  ),
  useBackwardation: createICReportQueryHook(
    'commodities_backwardation',
    (api) => api.icReport.commodities.getBackwardation,
  ),
  useBearBull: createICReportQueryHook(
    'bcom_bear_bull_chart',
    (api) => api.icReport.commodities.getBearBull,
  ),
}

//
// DIVERSIFIERS
//

export const diversifiers = {
  useBucketedAttributions: createICReportQueryHook(
    'attributions_bucketed_diversifiers',
    (api) => api.icReport.diversifiers.getBucketedAttributions,
  ),
  useRebased100Monthly: createICReportQueryHook(
    'perf_rebased_100',
    (api) => api.icReport.diversifiers.getRebased100Monthly,
  ),
  useRebased0Monthly: createICReportQueryHook(
    'monthly_perf_rebased_0',
    (api) => api.icReport.diversifiers.getRebased0Monthly,
  ),
  useRebased0Yearly: createICReportQueryHook(
    'yearly_perf_rebased_0',
    (api) => api.icReport.diversifiers.getRebased0Yearly,
  ),
  useContribution: createICReportQueryHook(
    'perf_contribution',
    (api) => api.icReport.diversifiers.getContribution,
  ),
  useMetrics: createICReportQueryHook('metrics', (api) => api.icReport.diversifiers.getMetrics),
  useFx: createICReportQueryHook('fx_diversifiers', (api) => api.icReport.diversifiers.getFx),
  useRatesVolatilities: createICReportQueryHook(
    'rates_volatilities',
    (api) => api.icReport.diversifiers.getRatesVolatilities,
  ),
  useHistoricalEvents: createICReportQueryHook(
    'historical_events_diversifiers',
    (api) => api.icReport.diversifiers.getHistoricalEvents,
  ),
  useHistoricalEventsNew: createICReportQueryHook(
    'scenario_analysis_diversifiers',
    (api) => api.icReport.diversifiers.getHistoricalEventsNew,
  ),
  useCtaExposure: createICReportQueryHook(
    'cta_exposure',
    (api) => api.icReport.diversifiers.getCtaExposure,
  ),
}

//
// PROTECTION
//

export const protection = {
  useOptions: createICReportQueryHook(
    'protection_options',
    (api) => api.icReport.protection.getOptions,
  ),
  useBreakdown: createICReportQueryHook(
    'protection_breakdown',
    (api) => api.icReport.protection.getBreakdown,
  ),
  useHistoricalEvents: createICReportQueryHook(
    'protection_scenario',
    (api) => api.icReport.protection.getHistoricalEvents,
  ),
  useHistoricalEventsNew: createICReportQueryHook(
    'scenario_analysis_protection',
    (api) => api.icReport.protection.getHistoricalEventsNew,
  ),
}
